import { z } from 'zod';
import { apiRequest } from './client';
import {
  AuditResponseSchema,
  BacktestDetailSchema,
  BacktestListResponseSchema,
  BacktestSummarySchema,
  CommandResultSchema,
  ConnectionTestResultSchema,
  ConnectionsResponseSchema,
  DashboardResponseSchema,
  DecisionsResponseSchema,
  HealthReadySchema,
  LoginResponseSchema,
  OrdersResponseSchema,
  PositionsResponseSchema,
  PreviewResponseSchema,
  SessionResponseSchema,
  StrategyConfigSchema,
  TradesResponseSchema,
  type AuditResponse,
  type BacktestDetail,
  type BacktestListResponse,
  type BacktestRequest,
  type BacktestSummary,
  type CommandResult,
  type ConnectionTestResult,
  type ConnectionsResponse,
  type DashboardResponse,
  type DecisionsResponse,
  type HealthReady,
  type LoginResponse,
  type OrdersResponse,
  type PositionsResponse,
  type PreviewRequest,
  type PreviewResponse,
  type SessionResponse,
  type StrategyConfig,
  type TradesResponse,
} from './schemas';

/**
 * Typed wrappers over every endpoint the dashboard uses.
 *
 * Each one names its schema explicitly, so adding a screen cannot accidentally
 * skip validation — there is no "raw fetch" escape hatch exported anywhere.
 */

const NoContentSchema = z.unknown();

// --- Auth / session --------------------------------------------------------

export interface LoginInput {
  readonly email: string;
  readonly password: string;
  readonly totpCode?: string;
}

export function login(input: LoginInput): Promise<LoginResponse> {
  return apiRequest('/auth/login', LoginResponseSchema, {
    method: 'POST',
    body: {
      email: input.email,
      password: input.password,
      totpCode: input.totpCode ?? null,
    },
  });
}

export function logout(): Promise<unknown> {
  return apiRequest('/auth/logout', NoContentSchema, { method: 'POST' });
}

export function fetchSession(signal?: AbortSignal): Promise<SessionResponse> {
  return apiRequest('/auth/session', SessionResponseSchema, signal === undefined ? {} : { signal });
}

export interface ReauthInput {
  readonly password: string;
  readonly totpCode?: string;
}

export function reauthenticate(input: ReauthInput): Promise<SessionResponse> {
  return apiRequest('/auth/reauth', SessionResponseSchema, {
    method: 'POST',
    body: { password: input.password, totpCode: input.totpCode ?? null },
  });
}

// --- Dashboard -------------------------------------------------------------

export function fetchDashboard(signal?: AbortSignal): Promise<DashboardResponse> {
  return apiRequest('/dashboard', DashboardResponseSchema, signal === undefined ? {} : { signal });
}

export function fetchHealth(signal?: AbortSignal): Promise<HealthReady> {
  return apiRequest('/health/ready', HealthReadySchema, signal === undefined ? {} : { signal });
}

// --- Strategy --------------------------------------------------------------

export function fetchStrategyConfig(signal?: AbortSignal): Promise<StrategyConfig> {
  return apiRequest(
    '/strategy/config',
    StrategyConfigSchema,
    signal === undefined ? {} : { signal },
  );
}

export function saveStrategyConfig(
  config: StrategyConfig,
  idempotencyKey: string,
): Promise<StrategyConfig> {
  return apiRequest('/strategy/config', StrategyConfigSchema, {
    method: 'PUT',
    body: config,
    idempotencyKey,
  });
}

export function previewDecision(overrides: PreviewRequest): Promise<PreviewResponse> {
  return apiRequest('/strategy/preview', PreviewResponseSchema, {
    method: 'POST',
    body: overrides,
  });
}

// --- Positions, orders, trades --------------------------------------------

/**
 * `GET /api/positions` answers with the standard `{items, page}` envelope, like
 * every other collection. The screens that show "the" position take
 * `items[0] ?? null`; they do not assume the list can only hold one, because
 * during a partial exit it can hold more.
 */
export function fetchPositions(signal?: AbortSignal): Promise<PositionsResponse> {
  return apiRequest('/positions', PositionsResponseSchema, signal === undefined ? {} : { signal });
}

export function fetchOrders(signal?: AbortSignal): Promise<OrdersResponse> {
  return apiRequest('/orders', OrdersResponseSchema, signal === undefined ? {} : { signal });
}

export function fetchTrades(signal?: AbortSignal): Promise<TradesResponse> {
  return apiRequest('/trades', TradesResponseSchema, signal === undefined ? {} : { signal });
}

// --- Decisions -------------------------------------------------------------

export interface DecisionFilter {
  readonly from?: string;
  readonly to?: string;
  readonly decision?: string;
}

export function fetchDecisions(
  filter: DecisionFilter,
  signal?: AbortSignal,
): Promise<DecisionsResponse> {
  return apiRequest('/decisions', DecisionsResponseSchema, {
    query: { from: filter.from, to: filter.to, decision: filter.decision },
    ...(signal === undefined ? {} : { signal }),
  });
}

// --- Audit & connections ---------------------------------------------------

export function fetchAudit(
  filter: { from?: string; to?: string },
  signal?: AbortSignal,
): Promise<AuditResponse> {
  return apiRequest('/audit', AuditResponseSchema, {
    query: { from: filter.from, to: filter.to },
    ...(signal === undefined ? {} : { signal }),
  });
}

export function fetchConnections(signal?: AbortSignal): Promise<ConnectionsResponse> {
  return apiRequest(
    '/connections',
    ConnectionsResponseSchema,
    signal === undefined ? {} : { signal },
  );
}

/**
 * `POST /api/connections/:id/test` -- "test", matching the RBAC permission
 * (`TEST_VENUE_CONNECTION`) and the OpenAPI summary. The client used to post to
 * `/check`, which no route served.
 *
 * The answer is a PROBE RESULT, not a connection row: it says what this one
 * check found. Callers refresh the connections list afterwards rather than
 * patching a row from the probe, so the list always shows server state.
 */
export function testConnection(id: string, idempotencyKey: string): Promise<ConnectionTestResult> {
  return apiRequest(`/connections/${encodeURIComponent(id)}/test`, ConnectionTestResultSchema, {
    method: 'POST',
    idempotencyKey,
  });
}

// --- Backtests -------------------------------------------------------------

export function fetchBacktests(signal?: AbortSignal): Promise<BacktestListResponse> {
  return apiRequest(
    '/backtests',
    BacktestListResponseSchema,
    signal === undefined ? {} : { signal },
  );
}

export function fetchBacktest(id: string, signal?: AbortSignal): Promise<BacktestDetail> {
  return apiRequest(
    `/backtests/${encodeURIComponent(id)}`,
    BacktestDetailSchema,
    signal === undefined ? {} : { signal },
  );
}

export function startBacktest(
  input: BacktestRequest,
  idempotencyKey: string,
): Promise<BacktestSummary> {
  return apiRequest('/backtests', BacktestSummarySchema, {
    method: 'POST',
    body: input,
    idempotencyKey,
  });
}

// --- Control commands ------------------------------------------------------

/**
 * All six commands live under `/api/commands/<kebab-case>`, one prefix, because
 * on the server that prefix is what guarantees the idempotency ledger, the
 * audit entry and the live gate. The client previously split them across
 * `/strategy/*` and `/commands/*`, and four of the six pointed at paths no
 * route served -- `/strategy/start` had no handler at all.
 *
 * Every command is idempotent by key. The UI must not present one as done
 * before this promise resolves; see `CommandResult.venueConfirmed`.
 */
function command(path: string, idempotencyKey: string, body?: unknown): Promise<CommandResult> {
  return apiRequest(`/commands/${path}`, CommandResultSchema, {
    method: 'POST',
    idempotencyKey,
    ...(body === undefined ? {} : { body }),
  });
}

export function startStrategy(idempotencyKey: string): Promise<CommandResult> {
  return command('start-strategy', idempotencyKey);
}

export function pauseEntries(idempotencyKey: string): Promise<CommandResult> {
  return command('pause-entries', idempotencyKey);
}

export function resumeEntries(idempotencyKey: string): Promise<CommandResult> {
  return command('resume-entries', idempotencyKey);
}

export function runNow(idempotencyKey: string): Promise<CommandResult> {
  return command('run-now', idempotencyKey);
}

export function emergencyClose(idempotencyKey: string): Promise<CommandResult> {
  return command('emergency-close', idempotencyKey);
}

export function cancelOrders(idempotencyKey: string): Promise<CommandResult> {
  return command('cancel-orders', idempotencyKey);
}
